import { useLocation, useNavigate } from "@tanstack/react-router";
import { createContext, type PropsWithChildren, useContext, useEffect, useState } from "react";
import { ApiError } from "./api";
import { MeApi } from "./api/me";
import { sanitizeRedirect } from "./auth-redirect";
import { classifyUserFetchFailure, isStoredSessionExpired } from "./auth-session";
import { authStore } from "./auth-store";
import type { Data } from "./data";
import { queryClient } from "./query-client";

// TODO: use this instead of `User.AuthUserContext` in `src/lib/db/user.tsx`
export namespace Auth {
  // TODO: use this.
  type Auth = {
    isLoading: boolean;
    isAuthenticated: boolean;
    user: Data.User | null; // change to non-nullable.
    /** External customer account (users.customer_id set): read-only, sees only own company's submitted reports. */
    isCustomerUser: boolean;
  };

  const AuthContext = createContext<Auth>(null!);

  export function useAuth() {
    const auth = useContext(AuthContext);
    return auth;
  }

  export function AuthProvider({ children }: PropsWithChildren) {
    const [user, setUser] = useState<Data.User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(authStore.isValid);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      function signOut() {
        // Clearing the store fires onChange, which drops the cached queries and
        // resets the context; do it explicitly too so the very first render
        // after mount (before the listener below is attached) is consistent.
        authStore.clear();
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
      }

      async function execGetUser(_id: string) {
        setIsLoading(true);
        let user: Data.User | undefined;
        try {
          // The id is no longer sent: /app/me answers for whoever the bearer
          // token resolves to, which is where the id came from in the first
          // place (ADR-0007 slice F).
          user = await MeApi.fetchMe<Data.User>();
        } catch (error) {
          console.error("Error getting user:", error);
          const status = error instanceof ApiError ? error.status : 0;
          switch (classifyUserFetchFailure(status, authStore.isValid)) {
            case "already-signed-out":
              // api() already cleared authStore on the 401 and fired onChange, which
              // set isAuthenticated=false. Just stop loading — RequireAuth will redirect.
              setIsLoading(false);
              return;
            case "sign-out":
              console.warn("Session is no longer usable, signing out:", status);
              signOut();
              return;
            case "keep-session":
              // Transient error (504, network issue, etc.) — keep the session,
              // use the stored auth record as a fallback so the user isn't kicked out.
              console.warn("Transient error fetching user, keeping session:", status);
              setUser(authStore.record as unknown as Data.User);
              setIsAuthenticated(true);
              setIsLoading(false);
              return;
          }
        }
        if (!user) {
          // A 200 with no user: the token authenticates something that is not
          // a portal user. The read this replaced answered that with a 404,
          // which the classifier mapped to "sign-out"; keep landing there.
          console.warn("Authenticated token has no portal user, signing out");
          signOut();
          return;
        }
        setUser(user);
        setIsAuthenticated(true);
        setIsLoading(false);
      }

      // A stored session whose token has expired must be dropped without asking
      // the server: PocketBase serves an unusable token as a guest and answers
      // rule-guarded reads with 404, so keeping it would render the app
      // signed-in with every fetch failing (FANL-1433).
      function dropExpiredSession() {
        if (!isStoredSessionExpired(!!authStore.record, authStore.isValid)) return false;
        console.warn("Stored session has expired, signing out");
        signOut();
        return true;
      }

      // Check if already authenticated on mount
      if (dropExpiredSession()) {
        // Nothing more to do — RequireAuth redirects to /login.
      } else if (authStore.record?.id) {
        execGetUser(authStore.record.id);
      } else {
        // Not authenticated, stop loading
        setIsLoading(false);
      }

      const unsub = authStore.onChange(async (_accessToken, authRecord) => {
        console.log("AuthProvider: auth changed, authRecord:", authRecord);
        if (authRecord?.id) {
          // Immediately set authenticated to true when auth changes
          console.log("AuthProvider: Setting authenticated, fetching user data");
          setIsAuthenticated(true);
          execGetUser(authRecord.id);
          return;
        } else {
          // User logged out
          console.log("AuthProvider: User logged out");
          // Drop all cached queries so the next principal on this device can't
          // be served the previous user's access-scoped data (e.g. economics).
          queryClient.clear();
          setIsAuthenticated(false);
          setUser(null);
          setIsLoading(false);
        }
      });

      // The token can lapse while the tab sits open for days. Nothing else would
      // notice: without a re-render the user record is never refetched, and the
      // 404s PocketBase returns for a guest look like missing records rather than
      // an ended session. The check is a local JWT expiry read, so it is cheap.
      const expiryCheck = setInterval(dropExpiredSession, 60_000);
      document.addEventListener("visibilitychange", dropExpiredSession);

      return () => {
        unsub();
        clearInterval(expiryCheck);
        document.removeEventListener("visibilitychange", dropExpiredSession);
      };
    }, []);

    return (
      <AuthContext.Provider value={{ user, isAuthenticated, isLoading, isCustomerUser: !!user?.customer_id }}>
        {children}
      </AuthContext.Provider>
    );
  }

  /**
   * Wrapper component that ensures user is authenticated before rendering children.
   * Shows loading spinner while auth is initializing.
   * Redirects to login if not authenticated.
   */
  export function RequireAuth({ children }: PropsWithChildren) {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const needsLogin = !auth.isLoading && (!auth.isAuthenticated || !auth.user);

    // Redirect to login if not authenticated. The page the user actually asked
    // for travels along as ?redirect= so a shared deep link (or a session that
    // lapsed mid-report) resumes there after sign-in instead of dumping them on
    // /mission/overview. `replace` keeps the guarded URL out of history, so the
    // back button doesn't bounce straight back into this redirect.
    //
    // The navigation has to happen in an effect: called during render it makes
    // the router commit a new location mid-render, which React answers with a
    // "cannot update a component while rendering" warning and the redirect
    // itself never settles.
    useEffect(() => {
      if (!needsLogin) return;
      // This guard stays mounted for a beat after the router has already moved
      // to /login, and the effect re-runs on the new location. Navigating a
      // second time from there would overwrite the ?redirect= just set.
      if (location.pathname === "/login") return;
      const redirect = sanitizeRedirect(location.href);
      navigate({ to: "/login", search: redirect ? { redirect } : {}, replace: true });
    }, [needsLogin, location.pathname, location.href, navigate]);

    // Show loading while auth is initializing
    if (auth.isLoading) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-gray-600 border-t-foreground dark:border-t-foreground"></div>
            <p className="text-muted-foreground text-sm">Laster...</p>
          </div>
        </div>
      );
    }

    // The effect above is taking the user to /login; render nothing meanwhile.
    if (needsLogin) {
      return null;
    }

    // User is authenticated, render children
    return <>{children}</>;
  }
}
